import React, { useRef, useState } from "react";
import { MAX_CHARACTERS } from "../../utils/constants";
import { useAudio } from "../../providers/AudioProvider";
import { useHandleCompareRequest } from "../../hooks/useHandleCompareRequest";
import { useSettings } from "../../providers/SettingsContext";

interface ChatInputProps {
  onMicClick?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ onMicClick }) => {
  const [inputText, setInputText] = useState("");
  const { mediaState, pause, play, setFullscreenVideoMessageId } = useAudio();
  const [isLoading] = useState(false);
  const { settings } = useSettings();
  const { handleCompareRequest } = useHandleCompareRequest({
    audio: { play, setFullscreenVideoMessageId },
  });

  // Ref for the textarea to enable auto-scrolling
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleMicClick = () => {
    // Pause any currently playing media when mic button is pressed
    const currentId = mediaState.getState().currentId;
    if (currentId) pause(currentId);

    // Show the conversation mode popup
    if (onMicClick) {
      onMicClick();
    }
  };

  const handleSubmit = async () => {
    if (!inputText.trim() || isLoading) return;
    const textToSend = inputText.trim();
    setInputText("");
    await handleCompareRequest(textToSend);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setInputText(value);
    }
  };

  const remainingCharacters = MAX_CHARACTERS - inputText.length;
  const isNearLimit = remainingCharacters <= 100;
  const isAtLimit = remainingCharacters === 0;

  return (
    <div className="flex flex-col p-2 sm:p-3 lg:p-4 border-t border-gray-200 flex-shrink-0">
      <div className="flex gap-2 sm:gap-3">
        <div className="h-fit relative w-full">
          <textarea
            ref={textareaRef}
            data-trust-trigger="text-input"
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask Aveta..."
            maxLength={MAX_CHARACTERS}
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#46ACC6] focus:border-transparent resize-none text-sm sm:text-base"
            rows={1}
            style={{ minHeight: "40px", maxHeight: "120px" }}
          />
          {/* Character counter */}
          <div className="absolute bottom-1 right-2 text-xs text-gray-400 pointer-events-none">
            <span
              className={
                isNearLimit
                  ? isAtLimit
                    ? "text-red-500 font-semibold"
                    : "text-orange-500"
                  : ""
              }
            >
              {remainingCharacters}
            </span>
            <span className="text-gray-400">/{MAX_CHARACTERS}</span>
          </div>
        </div>

        {/* Microphone button */}
        {settings.showMicButton && (
          <button
            onClick={handleMicClick}
            data-trust-trigger="mic-button"
            disabled={isLoading}
            className="px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 flex items-center justify-center min-w-[40px] sm:min-w-[48px] bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400"
          >
            <svg
              className="w-6 h-6 sm:w-8 sm:h-8"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
            </svg>
          </button>
        )}

        {/* Send button */}
        <button
          onClick={handleSubmit}
          data-trust-trigger="send-button"
          disabled={isLoading || !inputText.trim() || isAtLimit}
          className={`px-3 sm:px-4 py-2 sm:py-3 rounded-lg transition-all duration-200 flex items-center justify-center min-w-[40px] sm:min-w-[48px] ${
            inputText.trim() && !isAtLimit
              ? "bg-[#46ACC6] hover:bg-[#3a8ba3] text-white shadow-lg shadow-[#46ACC6]/30"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {isLoading ? (
            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg
              className="w-6 h-6 sm:w-8 sm:h-8"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
